/* eslint-disable no-undefined */
import { useCallback, useState, useEffect, useRef, useMemo } from 'react';

function useCallbackRef(callback) {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  });
  // https://github.com/facebook/react/issues/19240
  return useMemo(() => ((...args) => callbackRef.current?.(...args)), []);
}

// Heavily inspired by https://github.com/radix-ui/primitives
export default function useControllableState({
  prop,
  defaultProp,
  onChange = () => {}
}) {
  const [uncontrolledProp, setUncontrolledProp] = useUncontrolledState({
    defaultProp,
    onChange
  });
  const isControlled = prop !== undefined;
  const value = isControlled ? prop : uncontrolledProp;
  const handleChange = useCallbackRef(onChange);

  const setValue = useCallback(
    (nextValue) => {
      if (isControlled) {
        const setter = nextValue;
        const valueToCheck = typeof nextValue === 'function' ? setter(prop) : nextValue;
        if (valueToCheck !== prop) handleChange(valueToCheck);
      } else {
        setUncontrolledProp(nextValue);
      }
    },
    [isControlled, prop, handleChange, setUncontrolledProp]
  );

  return [value, setValue];
}

function useUncontrolledState({ defaultProp, onChange }) {
  const uncontrolledState = useState(defaultProp);
  const [value] = uncontrolledState;
  const prevValueRef = useRef(value);
  const handleChange = useCallbackRef(onChange);

  useEffect(() => {
    if (prevValueRef.current !== value) {
      handleChange(value);
      prevValueRef.current = value;
    }
  }, [value, prevValueRef, handleChange]);

  return uncontrolledState;
}
